import { api } from './api'

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract data from API envelope response
 * The API returns: { statusCode, response: { success, data, message } }
 */
function extractData<T = any>(apiResponse: any): T {
  // Response is nested in response.response
  const data = apiResponse?.response
  // Some endpoints return { success, data }, others return data directly
  return (data?.data || data) as T
}

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface StartAssessmentResponse {
  id: string
}

export interface BusinessDetails {
  businessId: string
  typeOfBusiness: 'self-employment' | 'uk-property' | 'foreign-property'
  tradingName?: string
  accountingPeriods?: Array<{
    start: string
    end: string
  }>
  firstAccountingPeriodStartDate?: string
  firstAccountingPeriodEndDate?: string
}

export interface Obligation {
  id: string
  businessId?: string | null
  typeOfBusiness: string
  periodStartDate: string
  periodEndDate: string
  dueDate: string
  status: string
  source?: string
  receivedDate?: string
}

export interface Employment {
  employmentId: string
  employerName: string
  employerRef?: string
  startDate?: string
  endDate?: string
  totalPayToDate?: number
  totalTaxToDate?: number
}

export interface TaxCalculation {
  calculationId: string
  calculationTimestamp: string
  taxCalculation: {
    incomeTax: {
      totalIncomeReceivedFromAllSources: number
      totalAllowancesAndDeductions: number
      totalTaxableIncome: number
      incomeTaxCharged: number
      incomeTaxDueAfterDeductions: number
      incomeTaxOverpaid?: number
    }
    nationalInsurance: {
      class2Nics?: { amount: number }
      class4Nics?: { amount: number }
    }
    totalIncomeTaxAndNicsDue: number
    totalStudentLoansRepaymentAmount?: number
  }
}

export interface AssessmentSummary {
  _id: string
  nino: string
  taxYear: string
  planId: string
  employments?: Employment[]
  selfEmployment?: any[]
  propertyBusiness?: any[]
  dividendsIncome?: any
}

// ============================================================================
// SELF ASSESSMENT API SERVICE
// ============================================================================

class SelfAssessmentApiService {
  // ==========================================================================
  // STEP 1: START ASSESSMENT & GET BUSINESS DETAILS
  // ==========================================================================

  /**
   * Start a new self-assessment or update existing one with pricing plan
   * This MUST be called first to get the assessmentId
   */
  async startAssessment(nino: string, planSlug: string): Promise<StartAssessmentResponse> {
    const response = await api.post(
      `/filling/assessments/self-employment/${nino}/start-self-assessment`,
      { planSlug },
      { skipAuthLogout: true } // Prevent logout if this fails - it only needs regular auth, not HMRC
    )
    return extractData<StartAssessmentResponse>(response)
  }

  /**
   * Get all businesses registered for Making Tax Digital
   * Note: This endpoint may require HMRC auth, but we don't want to logout if it fails
   */
  async getBusinessDetails(nino: string): Promise<BusinessDetails[]> {
    const response = await api.get(
      `/filling/assessments/business-details/${nino}/list`,
      { skipAuthLogout: true } // Don't logout on HMRC auth failures
    )
    const data = extractData<any>(response)
    return data?.businesses || []
  }

  /**
   * Get specific business details
   */
  async getBusinessById(nino: string, businessId: string): Promise<BusinessDetails> {
    const response = await api.get(`/filling/assessments/business-details/${nino}/${businessId}`)
    return extractData<BusinessDetails>(response)
  }

  // ==========================================================================
  // STEP 2: GET OBLIGATIONS & EMPLOYMENTS
  // ==========================================================================

  /**
   * Get quarterly reporting obligations for Making Tax Digital
   */
  async getIncomeAndExpenditureObligations(
    nino: string,
    params?: {
      typeOfBusiness?: 'self-employment' | 'uk-property' | 'foreign-property'
      businessId?: string
      fromDate?: string
      toDate?: string
      status?: 'open' | 'fulfilled'
    }
  ): Promise<Obligation[]> {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, String(value))
      })
    }
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(
      `/filling/assessments/obligations/${nino}/income-and-expenditure${queryString}`
    )
    const data = extractData<any>(response)
    return data?.obligations || []
  }

  /**
   * Get crystallisation (final declaration) obligations
   */
  async getCrystallisationObligations(
    nino: string,
    params?: {
      from?: string
      to?: string
      status?: 'Open' | 'Fulfilled'
    }
  ): Promise<any[]> {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, String(value))
      })
    }
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/filling/assessments/obligations/${nino}/crystallisation${queryString}`)
    const data = extractData<any>(response)
    return data?.obligations || []
  }

  /**
   * List all employments for a tax year
   */
  async getEmployments(nino: string, taxYear: string): Promise<Employment[]> {
    const response = await api.get(`/filling/assessments/employments-income/${nino}/${taxYear}`)
    const data = extractData<any>(response)
    return data?.employments || []
  }

  // ==========================================================================
  // STEP 3A: EMPLOYMENT INCOME
  // ==========================================================================

  /**
   * Create a custom employment
   */
  async createEmployment(
    nino: string,
    taxYear: string,
    data: {
      assessmentId: string
      employerName: string
      employerRef?: string
      startDate?: string
      cessationDate?: string
      payrollId?: string
      payFrequency?: 'monthly' | 'weekly'
      occupationalPension?: boolean
      totalPayToDate?: number
      totalTaxToDate?: number
    }
  ): Promise<Employment> {
    const response = await api.post(
      `/filling/assessments/employments-income/${nino}/${taxYear}`,
      data
    )
    return extractData<Employment>(response)
  }

  /**
   * Amend existing employment
   */
  async amendEmployment(
    nino: string,
    taxYear: string,
    employmentId: string,
    data: {
      assessmentId: string
      [key: string]: any
    }
  ): Promise<Employment> {
    const response = await api.put(
      `/filling/assessments/employments-income/${nino}/${taxYear}/${employmentId}`,
      data
    )
    return extractData<Employment>(response)
  }

  /**
   * Add employment financial details (pay, benefits)
   */
  async addEmploymentFinancialDetails(
    nino: string,
    taxYear: string,
    employmentId: string,
    data: {
      assessmentId: string
      employment: {
        pay: {
          taxablePayToDate: number
          totalTaxToDate: number
        }
        deductions?: {
          studentLoans?: {
            uglDeductionAmount?: number
            pglDeductionAmount?: number
          }
        }
        benefitsInKind?: Record<string, number>
        offPayrollWorker?: boolean
      }
    }
  ): Promise<any> {
    const response = await api.post(
      `/filling/assessments/employments-income/${nino}/${taxYear}/${employmentId}/financial-details`,
      data
    )
    return extractData(response)
  }

  // ==========================================================================
  // STEP 3B: SELF EMPLOYMENT INCOME
  // ==========================================================================

  /**
   * Create self-employment period summary (quarterly)
   */
  async createSelfEmploymentPeriod(
    nino: string,
    businessId: string,
    data: {
      assessmentId: string
      taxYear: string
      periodDates: {
        from: string
        to: string
      }
      periodIncome?: {
        turnover?: number
        other?: number
      }
      periodExpenses?: {
        [key: string]: { amount: number } | number
      }
      periodDisallowableExpenses?: {
        [key: string]: { amount: number } | number
      }
    }
  ): Promise<{ periodId: string; from: string; to: string }> {
    const response = await api.post(
      `/filling/assessments/self-employment/${nino}/${businessId}/period`,
      data
    )
    return extractData(response)
  }

  /**
   * Get self-employment period summary
   */
  async getSelfEmploymentPeriod(
    nino: string,
    businessId: string,
    taxYear: string,
    periodId: string
  ): Promise<any> {
    const response = await api.get(
      `/filling/assessments/self-employment/${nino}/${businessId}/period/${taxYear}/${periodId}`
    )
    return extractData(response)
  }

  /**
   * List all period summaries for a tax year
   */
  async listSelfEmploymentPeriods(
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<any[]> {
    const response = await api.get(
      `/filling/assessments/self-employment/${nino}/${businessId}/period/${taxYear}`
    )
    const data = extractData<any>(response)
    return data?.periods || []
  }

  /**
   * Create or amend self-employment annual submission
   */
  async createSelfEmploymentAnnual(
    nino: string,
    businessId: string,
    taxYear: string,
    data: {
      assessmentId: string
      adjustments?: {
        [key: string]: number
      }
      allowances?: {
        [key: string]: number
      }
      nonFinancials?: {
        class4NicInfo?: {
          isExempt?: boolean
          exemptionCode?: string
        }
        payeEmployed?: boolean
        directorships?: Array<{
          directorshipName: string
          accountPeriodStart: string
          accountPeriodEnd: string
        }>
      }
    }
  ): Promise<any> {
    const response = await api.put(
      `/filling/assessments/self-employment/${nino}/${businessId}/annual/${taxYear}`,
      data
    )
    return extractData(response)
  }

  /**
   * Get self-employment annual submission
   */
  async getSelfEmploymentAnnual(
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<any> {
    const response = await api.get(
      `/filling/assessments/self-employment/${nino}/${businessId}/annual/${taxYear}`
    )
    return extractData(response)
  }

  // ==========================================================================
  // STEP 3C: UK PROPERTY INCOME
  // ==========================================================================

  /**
   * Create UK property period summary
   */
  async createUKPropertyPeriod(
    nino: string,
    businessId: string,
    taxYear: string,
    data: {
      assessmentId: string
      from: string
      to: string
      financials: {
        incomes?: {
          [key: string]: { amount: number }
        }
        expenses?: {
          [key: string]: { amount: number }
        }
      }
    }
  ): Promise<{ submissionId: string; from: string; to: string }> {
    const response = await api.post(
      `/filling/assessments/property-business/uk/${nino}/${businessId}/period/${taxYear}`,
      data
    )
    return extractData(response)
  }

  /**
   * Create or amend UK property annual submission
   */
  async createUKPropertyAnnual(
    nino: string,
    businessId: string,
    taxYear: string,
    data: {
      assessmentId: string
      adjustments?: any
      allowances?: any
    }
  ): Promise<any> {
    const response = await api.put(
      `/filling/assessments/property-business/uk/${nino}/${businessId}/annual/${taxYear}`,
      data
    )
    return extractData(response)
  }

  // ==========================================================================
  // STEP 3D: DIVIDENDS INCOME
  // ==========================================================================

  /**
   * Create or amend dividends income
   */
  async createDividendsIncome(
    nino: string,
    taxYear: string,
    data: {
      assessmentId: string
      ukDividends?: number
      otherUkDividends?: number
      foreignDividends?: Array<{
        countryCode: string
        amountBeforeTax?: number
        taxTakenOff?: number
        specialWithholdingTax?: number
        foreignTaxCreditRelief?: boolean
        taxableAmount: number
      }>
      stockDividends?: {
        customerReference?: string
        grossAmount: number
      }
    }
  ): Promise<any> {
    const response = await api.put(
      `/filling/assessments/dividends-income/${nino}/${taxYear}`,
      data
    )
    return extractData(response)
  }

  /**
   * Get dividends income
   */
  async getDividendsIncome(nino: string, taxYear: string): Promise<any> {
    const response = await api.get(`/filling/assessments/dividends-income/${nino}/${taxYear}`)
    return extractData(response)
  }

  // ==========================================================================
  // STEP 4: GET ASSESSMENT SUMMARY
  // ==========================================================================

  /**
   * Get all self-assessment records for the user
   */
  async getAssessments(taxYear?: string): Promise<AssessmentSummary[]> {
    const queryString = taxYear ? `?taxYear=${taxYear}` : ''
    const response = await api.get(`/filling/self-assessment${queryString}`)
    return extractData<AssessmentSummary[]>(response)
  }

  // ==========================================================================
  // STEP 5: TAX CALCULATION
  // ==========================================================================

  /**
   * Trigger a tax calculation
   */
  async triggerCalculation(
    nino: string,
    taxYear: string,
    finalDeclaration: boolean = false,
    assessmentId?: string | null,
    options?: any
  ): Promise<{ calculationId: string }> {
    const response = await api.post(
      `/filling/assessments/self-assessment/calculations/${nino}/${taxYear}/trigger`,
      {
        finalDeclaration,
        assessmentId: assessmentId || undefined,
      },
      options
    )
    return extractData(response)
  }

  /**
   * Get tax calculation details
   */
  async getCalculation(
    nino: string,
    taxYear: string,
    calculationId: string
  ): Promise<TaxCalculation> {
    const response = await api.get(
      `/filling/assessments/self-assessment/calculations/${nino}/${taxYear}/${calculationId}`
    )
    const data = extractData<any>(response)
    return data?.calculation
  }

  /**
   * List all tax calculations
   */
  async listCalculations(
    nino: string,
    taxYear: string,
    calculationType?: string
  ): Promise<any[]> {
    const queryString = calculationType ? `?calculationType=${calculationType}` : ''
    const response = await api.get(
      `/filling/assessments/self-assessment/calculations/${nino}/${taxYear}${queryString}`
    )
    const data = extractData<any>(response)
    return data?.calculations || []
  }

  // ==========================================================================
  // STEP 7: ASSIST REPORT & FINAL SUBMISSION
  // ==========================================================================

  /**
   * Generate HMRC Assist Report
   */
  async generateAssistReport(
    nino: string,
    taxYear: string,
    calculationId: string
  ): Promise<{ reportId: string; correlationId: string }> {
    const response = await api.post(
      `/filling/assessments/self-assessment/assist/${nino}/${taxYear}/${calculationId}/report`
    )
    return extractData(response)
  }

  /**
   * Acknowledge Assist Report
   */
  async acknowledgeAssistReport(
    nino: string,
    reportId: string,
    correlationId: string
  ): Promise<{ success: boolean }> {
    const response = await api.post(
      `/filling/assessments/self-assessment/assist/${nino}/${reportId}/${correlationId}/acknowledge`
    )
    return extractData(response)
  }

  // ==========================================================================
  // ACCOUNT BALANCE & TRANSACTIONS
  // ==========================================================================

  /**
   * Get balance and transactions
   */
  async getBalanceAndTransactions(
    nino: string,
    params?: {
      docNumber?: string
      fromDate?: string
      toDate?: string
      onlyOpenItems?: boolean
      includeLocks?: boolean
      calculateAccruedInterest?: boolean
      removePOA?: boolean
      customerPaymentInformation?: boolean
      includeEstimatedCharges?: boolean
    }
  ): Promise<{
    balanceDetails: {
      overdueAmount: number
      payableAmount: number
      pendingAmount: number
    }
    transactions: Array<{
      transactionId: string
      documentId: string
      documentDate: string
      documentDescription: string
      totalAmount: number
      outstandingAmount: number
    }>
  }> {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) queryParams.append(key, String(value))
      })
    }
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(
      `/filling/assessments/self-assessment/accounts/${nino}/balance-and-transactions${queryString}`
    )
    return extractData(response)
  }

  /**
   * Get payments and allocations
   */
  async getPaymentsAndAllocations(
    nino: string,
    fromDate: string,
    toDate: string,
    params?: {
      paymentLot?: string
      paymentLotItem?: string
    }
  ): Promise<any> {
    const queryParams = new URLSearchParams({ fromDate, toDate })
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value)
      })
    }
    const queryString = `?${queryParams.toString()}`
    const response = await api.get(
      `/filling/assessments/self-assessment/accounts/${nino}/payments-and-allocations${queryString}`
    )
    return extractData(response)
  }
}

// Export singleton instance
const selfAssessmentApiService = new SelfAssessmentApiService()
export default selfAssessmentApiService
